import React, { useState, type ReactNode, type UIEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  STUDENT_JOIN_OFFERS_QUERY_KEY,
  USER_PROFILES_QUERY_KEY,
  USER_QUERY_KEY,
} from '@/api/queryKeys';
import { Header } from '@/components/Header';
import { ProfileMenuProvider } from '@/components/ProfileMenu/ProfileMenuProvider';
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator';
import { Sidebar } from '@/components/Sidebar';
import { MobileNavbar } from '@/components/Sidebar/MobileNavbar';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';

const HEADER_SHADOW_OFFSET_PX = 10;

const gateQueryKeys: string[] = [
  USER_QUERY_KEY[0],
  USER_PROFILES_QUERY_KEY[0],
  STUDENT_JOIN_OFFERS_QUERY_KEY[0],
];

interface Props {
  desktopHeader?: ReactNode;
  mobileHeader?: ReactNode;
  children?: ReactNode;
  hideMobileHeader?: boolean;
  hideMobileNavbar?: boolean;
  disallowPullToRefresh?: boolean;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
}

export const Layout: React.FC<Props> = ({
  desktopHeader,
  mobileHeader,
  children,
  hideMobileHeader = false,
  hideMobileNavbar = false,
  disallowPullToRefresh = false,
  onScroll,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const { contentOffset, pullProgress, isRefreshing, isPulling, pullProps } =
    usePullToRefresh({
      isEnabled: isMobile && !disallowPullToRefresh,
      onRefresh: () =>
        queryClient.refetchQueries({
          type: 'active',
          predicate: (query) =>
            !gateQueryKeys.includes(String(query.queryKey[0])),
        }),
    });

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > HEADER_SHADOW_OFFSET_PX);
    onScroll?.(event);
  };

  return (
    <ProfileMenuProvider>
      <div className="bg-background">
        <Sidebar />
        <main
          className={`flex h-svh flex-col max-md:animate-zoom-in md:pt-0 md:pl-[92px] lg:pl-[250px] print:h-auto ${hideMobileHeader ? '' : 'pt-[calc(4px+env(safe-area-inset-top))]'}`}
        >
          <Header
            hasShadow={isScrolled}
            hideOnMobile={hideMobileHeader}
            desktopHeader={desktopHeader}
            mobileHeader={mobileHeader}
          />
          <div className="relative flex min-h-0 flex-1 flex-col">
            <PullToRefreshIndicator
              progress={pullProgress}
              isRefreshing={isRefreshing}
              isPulling={isPulling}
            />
            <div
              onScroll={handleScroll}
              {...pullProps}
              style={{
                transform: contentOffset
                  ? `translateY(${contentOffset}px)`
                  : undefined,
                transition: isPulling ? 'none' : 'transform 200ms',
              }}
              className={`flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain md:pb-0 print:flex-none print:overflow-visible ${
                hideMobileNavbar
                  ? 'pb-[env(safe-area-inset-bottom)]'
                  : 'pb-[calc(64px+env(safe-area-inset-bottom))]'
              }`}
            >
              {children}
            </div>
          </div>
        </main>
        {!hideMobileNavbar && <MobileNavbar />}
      </div>
    </ProfileMenuProvider>
  );
};
